import clsx from 'clsx'
import {
  addDays,
  differenceInCalendarDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWeekend,
  isWithinInterval,
  max,
  min,
  subDays
} from 'date-fns'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  Link as ReactRouterLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams
} from 'react-router-dom'

import {
  ApiAccountSnapshot,
  ApiOperationType,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery
} from '@/api/rtk-api'
import CalendarIcon from '@/assets/calendar.svg?react'
import CheckIcon from '@/assets/tick-thick-white.svg?react'
import { Card, DatePicker, Icon, Link, Rule, Tabs } from '@/components/core-v2'
import { PATH, TENANT } from '@/const'
import { getOperationType } from '@/utilities/mappers'

import { HoldingsTable } from '../../components/holdings-table'
import { ParametersTable } from '../../components/parameters-table'
import { TradeEventsEvents } from '../../components/trade-events-events'
import { TradeEventsStatistics } from '../../components/trade-events-statistics'
import { TradeTemplate } from '../../templates/trade-template'
import classes from './index.module.scss'
import { Loading } from './loading'

// The day grid starts on column 2 because the "prev" button is before it
export const DAY_GRID_START = 2
export const ROW_HEIGHT = '52px'

export interface Row {
  doesContinueInFuture: boolean
  doesContinueInPast: boolean
  end: Date
  id: string
  isCompleted: boolean
  readableType: string
  start: Date
  type: ApiOperationType
}

export const ReportingTradeEventsPage = () => {
  const { '*': splat, date: stringDate, source, tradeId } = useParams()
  const [selectedDate, setSelectedDate] = useState(new Date(stringDate))
  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: source
  })
  const [tab, eventId] = splat.split('/')
  const form = useForm()
  const navigate = useNavigate()

  // Change selectedDate value when URL params change
  useEffect(() => {
    setSelectedDate(new Date(stringDate))
    form.setValue('date-picker', new Date(stringDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringDate])

  // Redirect on date picker value change
  const datePickerValue = form.watch('date-picker')
  useEffect(() => {
    if (datePickerValue && !isSameDay(selectedDate, datePickerValue)) {
      navigate(PATH.reportsSingleTradeEventsHoldings(source, tradeId, datePickerValue))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePickerValue])

  if (isLoading) return <Loading />

  const { accountSnapShots } = data
  const firstSnapshot = accountSnapShots[0] as ApiAccountSnapshot
  const lastSnapshot = accountSnapShots.at(-1) as ApiAccountSnapshot

  const snapshot = accountSnapShots.find(snapshot =>
    isSameDay(selectedDate, new Date(snapshot.valuationDate))
  )
  const snapshotIndex = accountSnapShots.findIndex(snapshot =>
    isSameDay(selectedDate, new Date(snapshot.valuationDate))
  )

  if (!snapshot) {
    return (
      <Navigate
        to={PATH.reportsSingleTradeEventsHoldings(
          source,
          tradeId,
          new Date(lastSnapshot.valuationDate)
        )}
      />
    )
  }

  const snapshotDate = new Date(snapshot.valuationDate)
  const getOperationsByDate = (date: Date) =>
    (accountSnapShots as ApiAccountSnapshot[]).find(snapshot =>
      isSameDay(new Date(snapshot.valuationDate), date)
    )?.operations || []
  const weekDates = [
    subDays(snapshotDate, 3),
    subDays(snapshotDate, 2),
    subDays(snapshotDate, 1),
    snapshotDate,
    addDays(snapshotDate, 1),
    addDays(snapshotDate, 2),
    addDays(snapshotDate, 3)
  ]
  const [firstDateOfWeek] = weekDates
  const lastDateOfWeek = weekDates[6]
  const prevDate = accountSnapShots.slice(0, snapshotIndex).at(-1)?.valuationDate
  const nextDate = accountSnapShots.slice(snapshotIndex + 1).at(0)?.valuationDate

  // When calculating the rows, we need to look a few days in to the future
  // to see if the events complete
  const weekDatesWithFuture = [
    ...weekDates,
    addDays(snapshotDate, 4),
    addDays(snapshotDate, 5),
    addDays(snapshotDate, 6),
    addDays(snapshotDate, 7),
    addDays(snapshotDate, 8),
    addDays(snapshotDate, 9),
    addDays(snapshotDate, 10)
  ]
  const allowedEventTypes: ApiOperationType[] = [
    'Deposit',
    'Withdrawal',
    'ProtectedWithdrawal',
    'FeeDeduction',
    'Rebalancing',
    'Dividend',
    'ReinvestmentConfirmation',
    'Redemption',
    'SwitchIn',
    'SwitchOut',
    'ProtectedSwitchOut',
    'TriggeredFeeDeduction',
    'ExternalTransfer',
    'GapPayment',
    'ProtectedAccountClosure',
    'AccountClosure',
    'OrderCorrection'
  ]

  const rows: Row[] = weekDatesWithFuture.reduce((acc, date) => {
    const ops = getOperationsByDate(date).filter(
      op => allowedEventTypes.includes(op.type) && op.state !== 'Rejected'
    )

    ops.forEach(op => {
      const existing = acc.find(row => row?.id === op.operationId)
      const isFeeDeduction = ['TriggeredFeeDeduction', 'FeeDeduction'].includes(op.type)
      if (existing) {
        existing.end = isAfter(date, lastDateOfWeek) ? lastDateOfWeek : date
        existing.isCompleted = op.state === 'Completed'
        existing.doesContinueInFuture = isAfter(date, lastDateOfWeek)
      } else if (weekDates.includes(date)) {
        // Add new rows if the date is in the weekDates.
        // Do not add a new if the date is one of the "future" dates that
        // we use to confirm if something has completed or not.
        if (!isFeeDeduction) {
          acc.push({
            readableType: getOperationType(op),
            type: op.type,
            id: op.operationId,
            start: new Date(op.generatedDate),
            end: date,
            isCompleted: op.state === 'Completed',
            doesContinueInPast: isBefore(new Date(op.generatedDate), firstDateOfWeek)
          })
        }
      }

      // TriggeredFeeDeductions are handled a little differently to the other types, and we should only
      // show one event entry (1 day) on the day it completes
      if (isFeeDeduction && op.state === 'Completed') {
        const existingFD = acc.find(
          row => row?.type === 'TriggeredFeeDeduction' || row?.type === 'FeeDeduction'
        )
        if (
          !existingFD &&
          isWithinInterval(date, { start: firstDateOfWeek, end: lastDateOfWeek })
        ) {
          acc.push({
            readableType: getOperationType(op),
            type: op.type,
            id: op.operationId,
            start: date,
            end: date,
            isCompleted: true,
            doesContinueInPast: false
          })
        }
      }
    })

    return acc
  }, [])

  const rowsInterestingSelectedDate = rows.filter(row => {
    const start = min([row.start, row.end])
    const end = max([row.start, row.end])
    return isWithinInterval(selectedDate, { start, end })
  })

  return (
    <TradeTemplate tabValue="events">
      <div className={classes.datePickerWrapper}>
        <span className={classes.selectedDateHeading}>
          {format(selectedDate, 'EEEE d MMMM yyyy')}
        </span>
        <FormProvider {...form}>
          <DatePicker
            key={stringDate}
            closeOnClick
            isPopover
            alignPopover="center"
            customTriggerElement={
              <span className={classes.pickAnotherDate}>
                <CalendarIcon />
                Pick another date
              </span>
            }
            defaultValue={selectedDate}
            id="date-picker"
            mapDays={({ date: dateObject }) => {
              const date = new Date(dateObject as any)
              const isDisabled = !accountSnapShots.some(snapshot =>
                isSameDay(new Date(snapshot.valuationDate), date)
              )
              return {
                className: clsx({
                  [classes.dayDisabled]: isDisabled,
                  [classes.hasEvent]: getOperationsByDate(date).length > 0
                }),
                disabled: isDisabled
              }
            }}
            maxDate={lastSnapshot.valuationDate}
            minDate={firstSnapshot.valuationDate}
            showClearDatesButton={false}
          />
        </FormProvider>
      </div>
      <Card.Wrapper className={classes.wrapper}>
        <TradeEventsStatistics snapshot={snapshot} />

        <Rule className={classes.rule} />
        <div className={clsx(classes.eventsWrapper)}>
          <div
            className={classes.eventGridWrapper}
            style={{ gridTemplateRows: `100px repeat(${rows.length}, ${ROW_HEIGHT}) 24px` }}
          >
            {prevDate ? (
              <Link
                className={classes.prevBtn}
                to={PATH.reportsSingleTradeEventsHoldings(source, tradeId, new Date(prevDate))}
                variant="link"
              >
                <Icon className="mr-half" name="chevronLeft" />
                prev
              </Link>
            ) : (
              <span className={clsx(classes.prevBtn, classes.btnDisabled)}>
                <Icon className="mr-half" name="chevronLeft" /> prev
              </span>
            )}

            {weekDates.map((date, index) => {
              const formattedDate = format(date, 'dd-MM-yyyy')
              const isDisabled = !accountSnapShots.some(snapshot =>
                isSameDay(new Date(snapshot.valuationDate), date)
              )
              return (
                <ReactRouterLink
                  key={formattedDate}
                  aria-disabled={isDisabled}
                  className={clsx(classes.dayWrapper, {
                    [classes.disabled]: isDisabled,
                    [classes.today]: isSameDay(date, snapshotDate)
                  })}
                  style={{ gridColumnStart: index + DAY_GRID_START }}
                  to={PATH.reportsSingleTradeEventsHoldings(source, tradeId, date)}
                >
                  <h4
                    className={clsx(classes.dateHeading, {
                      [classes.disabled]: isDisabled,
                      [classes.today]: isSameDay(date, snapshotDate)
                    })}
                  >
                    <span className={classes.number}>{format(date, 'dd')}</span>
                    <span className={classes.day}>{format(date, 'EEEE')}</span>
                  </h4>
                </ReactRouterLink>
              )
            })}
            {rows.map((row, rowIndex) => {
              const startWeekDateIndex = weekDates.findIndex(weekDate =>
                isSameDay(row.start, weekDate)
              )
              const startColumn =
                startWeekDateIndex === -1 ? DAY_GRID_START : startWeekDateIndex + DAY_GRID_START
              const startDate = isBefore(row.start, firstDateOfWeek) ? firstDateOfWeek : row.start
              const daySpan = differenceInCalendarDays(row.end, startDate) + 1
              const startRow = rowIndex + 2

              const cellDates = new Array(daySpan)
                .fill(null)
                .map((a, index) => addDays(startDate, index))

              const cells = cellDates.map((cellDate, cellIndex) => {
                const isStart = cellIndex === 0
                const isEnd = cellIndex === cellDates.length - 1
                const isMiddle = !isStart && !isEnd
                const isDisabled = !accountSnapShots.some(snapshot =>
                  isSameDay(new Date(snapshot.valuationDate), cellDate)
                )

                return (
                  <ReactRouterLink
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${row.id}-${cellIndex}-cell`}
                    className={clsx(classes.cell, {
                      [classes.completed]: row.isCompleted,
                      [classes.continuesPast]: row.doesContinueInPast,
                      [classes.continuesFuture]: row.doesContinueInFuture,
                      [classes.start]: isStart,
                      [classes.middle]: isMiddle,
                      [classes.end]: isEnd,
                      [classes.disabled]: isDisabled,
                      [classes.unselected]: Boolean(eventId) && eventId !== row.id,
                      [classes.selected]: Boolean(eventId) && eventId === row.id
                    })}
                    style={{
                      gridRowStart: `${startRow}`,
                      gridColumn: `${startColumn + cellIndex} / span 1`
                    }}
                    to={PATH.reportsSingleTradeEventsEvent(source, tradeId, cellDate, row.id)}
                  />
                )
              })

              const firstNonWeekendDateIndex = cellDates.findIndex(date => !isWeekend(date))
              const cellDatesFromFirstWeekend = cellDates.slice(firstNonWeekendDateIndex)
              const cellDatesSpanBetweenWeekends = cellDatesFromFirstWeekend.findIndex(date =>
                isWeekend(date)
              )
              const daySpanWithoutWeekends =
                cellDatesSpanBetweenWeekends === -1
                  ? cellDatesFromFirstWeekend.length
                  : cellDatesSpanBetweenWeekends

              const text = (
                <div
                  className={clsx(`${row.id}-cellText`, classes.cellText, {
                    [classes.unselected]: Boolean(eventId) && eventId !== row.id,
                    [classes.selected]: Boolean(eventId) && eventId === row.id
                  })}
                  style={{
                    gridRowStart: `${startRow}`,
                    gridColumn: `${
                      firstNonWeekendDateIndex + startColumn
                    } / span ${daySpanWithoutWeekends}`
                  }}
                >
                  {row.isCompleted && (
                    <div className={classes.checkIcon}>
                      <CheckIcon />
                    </div>
                  )}
                  <span>{row.readableType}</span>
                </div>
              )

              return (
                <>
                  {cells}
                  {text}
                </>
              )
            })}
            {nextDate ? (
              <Link
                className={classes.nextBtn}
                to={PATH.reportsSingleTradeEventsHoldings(source, tradeId, new Date(nextDate))}
                variant="link"
              >
                next <Icon className="ml-half" name="chevronRight" />
              </Link>
            ) : (
              <span className={clsx(classes.nextBtn, classes.btnDisabled)}>
                next <Icon className="ml-half" name="chevronRight" />
              </span>
            )}
          </div>
        </div>

        <div className={classes.tabsWrapper}>
          <Tabs.List aria-label="Events tabs" className="mt-12" ruleClassName="mb-10" spacing="md">
            <Tabs.Tab
              isSelected={tab === 'holdings'}
              to={PATH.reportsSingleTradeEventsHoldings(source, tradeId, selectedDate)}
              value="holdings"
            >
              Holdings
            </Tabs.Tab>
            <Tabs.Tab
              isSelected={tab === 'parameters'}
              to={PATH.reportsSingleTradeEventsParameters(source, tradeId, selectedDate)}
              value="parameters"
            >
              Parameters
            </Tabs.Tab>
            {rowsInterestingSelectedDate.length > 0 && (
              <Tabs.Tab
                isSelected={tab === 'events'}
                to={PATH.reportsSingleTradeEventsEvent(
                  source,
                  tradeId,
                  selectedDate,
                  rowsInterestingSelectedDate[0].id
                )}
                value="events"
              >
                Events
              </Tabs.Tab>
            )}
          </Tabs.List>
          <Routes>
            <Route
              element={
                <Tabs.Panel value="holdings">
                  <HoldingsTable />
                </Tabs.Panel>
              }
              path="holdings"
            />
            <Route
              element={
                <Tabs.Panel value="parameters">
                  <ParametersTable />
                </Tabs.Panel>
              }
              path="parameters"
            />
            <Route
              element={
                <Tabs.Panel value="events">
                  <TradeEventsEvents rows={rowsInterestingSelectedDate} />
                </Tabs.Panel>
              }
              path="events/:operationId"
            />
          </Routes>
        </div>
      </Card.Wrapper>
    </TradeTemplate>
  )
}
